import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Heart } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_c2c-nonprofit/artifacts/wlpq4j5e_c2c-1.png";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About Us", path: "/about" },
  { name: "Testimonials", path: "/testimonials" },
  { name: "Gallery", path: "/gallery" },
  { name: "Contact Us", path: "/contact" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "navbar-glass shadow-lg" : "bg-white/80 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
            <img
              src={LOGO_URL}
              alt="Confidence 2 Create Logo"
              className="h-16 w-16 object-contain"
            />
            <div className="hidden sm:block">
              <span className="font-bold text-[#0033A0] text-lg leading-tight block">
                Confidence2Create
              </span>
              <span className="text-[#FFC107] text-xs font-semibold tracking-wide" style={{ WebkitTextStroke: '0.5px black', paintOrder: 'stroke fill' }}>
                Inc.
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`nav-link font-semibold text-sm transition-colors ${
                  location.pathname === link.path
                    ? "text-[#0033A0]"
                    : "text-slate-600 hover:text-[#0033A0]"
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/donations"
              data-testid="nav-donate-btn"
              className="btn-secondary flex items-center gap-2 text-sm"
            >
              <Heart size={16} />
              Donate
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
            className="lg:hidden p-2 text-[#0033A0] hover:bg-slate-100 rounded-lg transition-colors"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-slate-200"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`block py-3 px-4 rounded-lg font-semibold transition-colors ${
                    location.pathname === link.path
                      ? "bg-[#0033A0] text-white"
                      : "text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/donations"
                data-testid="mobile-nav-donate-btn"
                className="btn-secondary flex items-center justify-center gap-2 w-full mt-4"
              >
                <Heart size={18} />
                Donate Now
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
